import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { HistoryCard } from '@/components/history/HistoryCard';
import { HistoryModal } from '@/components/history/HistoryModal';
import type { HistoryColumn, HistoryRow } from '@/components/history/types';
import { useLoanQuery } from '@/api/query/loan';
import type { Loan, LoanPayment } from '@/types';
import { formatCurrency, formatPaymentDate, paymentTypeLabel } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';

interface LoanHistoryProps {
  /** "#27 - Arun Prakash · P2701", shown in the View All modal. */
  subtitle: string;
  activeLoan?: Loan;
  closedLoans: Loan[];
}

const PAYMENT_COLUMNS: HistoryColumn[] = [
  { label: 'Month' },
  { label: 'Paid On' },
  { label: 'Principal' },
  { label: 'Interest' },
  { label: 'Total' },
  { label: 'Balance' },
  { label: 'Mode' },
];

const paymentRows = (payments: LoanPayment[]): HistoryRow[] =>
  payments.map((payment) => ({
    key: payment.id,
    cells: [
      formatMonthKey(payment.forMonth),
      formatPaymentDate(payment.paidOn),
      formatCurrency(payment.principal),
      formatCurrency(payment.interest),
      <b key="total">{formatCurrency(payment.principal + payment.interest)}</b>,
      formatCurrency(payment.balanceAfter),
      <span key="mode" className="text-gray-900">{paymentTypeLabel(payment.paymentType)}</span>,
    ],
  }));

/** The payments of one closed loan, loaded when its View link is clicked. */
function ClosedLoanPayments({ loan, subtitle, onClose }: { loan: Loan; subtitle: string; onClose: () => void }) {
  const { data, isLoading } = useLoanQuery(loan.id, true);
  const tab = {
    id: 'payments',
    label: `${loan.loanNo} · Payment History`,
    empty: 'No payments on this loan.',
    columns: PAYMENT_COLUMNS,
    rows: paymentRows(data?.payments ?? []),
  };

  return (
    <HistoryModal
      open
      onOpenChange={(open) => !open && onClose()}
      title={tab.label}
      subtitle={`${subtitle} · ${formatCurrency(loan.amount)} given ${formatPaymentDate(loan.givenOn)}`}
      tabs={[tab]}
      activeId={tab.id}
      onTabChange={() => undefined}
      loading={isLoading}
    />
  );
}

/** Payment History of the open loan and Loan History of closed loans, in the shared history layout. */
export function LoanHistory({ subtitle, activeLoan, closedLoans }: LoanHistoryProps) {
  const [viewLoan, setViewLoan] = useState<Loan | null>(null);

  return (
    <>
      <HistoryCard
        title="Policy Loan"
        subtitle={subtitle}
        defaultTabId={activeLoan ? 'payments' : 'loans'}
        tabs={[
          {
            id: 'payments',
            label: 'Payment History',
            empty: activeLoan ? 'No payments on this loan yet.' : 'No active loan on this policy.',
            columns: PAYMENT_COLUMNS,
            rows: paymentRows(activeLoan?.payments ?? []),
          },
          {
            id: 'loans',
            label: 'Loan History',
            empty: 'No loans taken on this policy yet.',
            columns: [
              { label: 'Loan No' },
              { label: 'Loan Amount' },
              { label: 'Given On' },
              { label: 'Closed' },
              { label: 'Interest Paid' },
              { label: 'Total Paid' },
              { label: '', align: 'right' },
            ],
            rows: closedLoans.map((loan) => ({
              key: loan.id,
              cells: [
                <b key="no">{loan.loanNo}</b>,
                formatCurrency(loan.amount),
                formatPaymentDate(loan.givenOn),
                formatMonthKey(loan.closedMonth),
                formatCurrency(loan.interestPaid),
                <b key="total">{formatCurrency(loan.amount + loan.interestPaid)}</b>,
                <Button key="view" type="button" variant="link" className="h-auto p-0 text-[#4369a0]" onClick={() => setViewLoan(loan)}>
                  View
                </Button>,
              ],
            })),
          },
        ]}
      />
      {viewLoan && <ClosedLoanPayments loan={viewLoan} subtitle={subtitle} onClose={() => setViewLoan(null)} />}
    </>
  );
}
